import React, { useState, useEffect, useRef } from 'react';
import { formatWithPlaceholders } from '../utils/text';

function InterpretationForm({ onSubmit, timer, copy = {}, language = 'he' }) {
  const [text, setText] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const deadlineRef = useRef(null);

  const direction = language === 'he' ? 'rtl' : 'ltr';
  const timeRemainingTemplate = copy.timeRemaining
    || (language === 'he'
      ? 'נותרו {{seconds}} שניות להגיש פרשנות'
      : 'You have {{seconds}} seconds left to submit an interpretation');
  const submitButton = copy.submitButton
    || (language === 'he' ? 'שליחת פרשנות' : 'Submit Interpretation');
  const submittedText = copy.submitted
    || (language === 'he' ? 'הפרשנות נשלחה!' : 'Interpretation sent!');
  const timeUpText = copy.timeUp
    || (language === 'he' ? 'נגמר הזמן — ממתינים לסיום הסבב' : 'Time is up — waiting for the round to finish');
  const displaySeconds = timer == null ? '…' : timeLeft;

  // אתחול מחדש בתחילת כל סבב
  useEffect(() => {
    if (timer == null) {
      deadlineRef.current = null;
      setTimeLeft(0);
      setSubmitted(false);
      setText('');
      return;
    }

    const now = Date.now();
    const seconds = typeof timer === 'number'
      ? timer
      : (timer.durationSeconds ?? timer.seconds ?? 0);
    const baseDurationMs = seconds * 1000;
    let remainingMs = baseDurationMs;

    if (typeof timer === 'object' && timer !== null) {
      const serverStartedAt = timer.startedAt;
      if (typeof serverStartedAt === 'number') {
        const elapsed = now - serverStartedAt;
        if (elapsed > 0) {
          remainingMs = Math.max(0, baseDurationMs - elapsed);
        }
      }
    }

    deadlineRef.current = now + remainingMs;
    setTimeLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
    setSubmitted(false);
    setText('');
  }, [timer]);

  // טיימר שניות להגשה
  useEffect(() => {
    if (!deadlineRef.current || submitted) return;

    let intervalId;

    const tick = () => {
      const remainingMs = deadlineRef.current - Date.now();
      if (remainingMs <= 0) {
        setTimeLeft(0);
        deadlineRef.current = null;
        clearInterval(intervalId);
      } else {
        setTimeLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
      }
    };

    tick();
    intervalId = setInterval(tick, 250);
    return () => clearInterval(intervalId);
  }, [submitted, timer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitted || text.trim() === '') return;
    onSubmit(text);
    setSubmitted(true);
  };

  return (
    <div dir={direction}>
      <p>{formatWithPlaceholders(timeRemainingTemplate, { seconds: displaySeconds })}</p>
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          width: '100%',
          maxWidth: 'min(100%, 560px)',
        }}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={submitted || timer == null || timeLeft <= 0}
          rows={5}
          wrap="soft"
          style={{
            width: '100%',
            minHeight: '7.5rem',
            padding: '0.75rem',
            fontSize: '1rem',
            lineHeight: '1.5',
            borderRadius: '10px',
            border: '1px solid #ccd0d5',
            boxSizing: 'border-box',
            resize: 'vertical',
            backgroundColor: submitted || timer == null || timeLeft <= 0 ? '#f1f3f5' : '#fff',
          }}
          autoFocus
        />
        <button type="submit" disabled={submitted || timer == null || timeLeft <= 0 || text.trim() === ''}>
          {submitButton}
        </button>
      </form>
      {submitted && <p>{submittedText}</p>}
      {timer != null && timeLeft <= 0 && !submitted && <p>{timeUpText}</p>}
    </div>
  );
}

export default InterpretationForm;

